import {
  ArrowDownToLine,
  ArrowUpToLine,
  Bell,
  CircleDot,
  Coins,
  Copy,
  Dices,
  Eye,
  EyeOff,
  Flag,
  Hand,
  Keyboard,
  Layers,
  LogOut,
  Maximize2,
  Minus,
  Mountain,
  Play,
  Plus,
  Power,
  RefreshCw,
  Repeat2,
  Replace,
  RotateCcw,
  RotateCw,
  ScrollText,
  Search,
  Shuffle,
  Skull,
  Sparkles,
  Tag,
  Trash2,
} from 'lucide-react';
import { CARD_COUNTER_ICONS, CARD_MARKER_ICONS, COUNTER_LABELS, MARKER_LABELS } from './cardLabels';

/**
 * Aktionen des Bretts als Daten: Kontextmenü (Rechner), Langdruck-Sheet und „Mehr"-Liste (Handy)
 * rendern dieselben Listen, damit Einträge, Reihenfolge und Beschriftung nur hier stehen.
 *
 * Eintrag: `{ id, label, icon, onSelect, danger?, children?, shortcut?, badge?, checked?,
 * disabled?, separatorBefore?, confirm? }`
 * - `children`: Untermenü (Rechner) bzw. Abschnitt (Sheet); ein solcher Eintrag hat kein `onSelect`.
 * - `shortcut`: Taste, die die Seite für dieselbe Aktion belegt (nur Anzeige).
 * - `badge`: Zahl am Eintrag (Counter-Stand), `checked`: gesetzter Marker.
 * - `separatorBefore`: Trennlinie vor dem Eintrag im Menü; das Sheet ignoriert sie.
 * - `confirm`: `{ title, description, confirmLabel }` — der Renderer fragt per `ConfirmDialog`
 *   nach und ruft `onSelect` erst nach der Bestätigung.
 *
 * Fehlt im `ctx` der Rückruf eines Eintrags, entfällt der Eintrag. `ctx` der Karten-Listen:
 *   Socket-Hook: tapCard, setFaceDown, transformCard, addCounter, removeCounter, addMarker,
 *     removeMarker, createCardCopy, moveCardZone, returnCardControl, requestCardControl,
 *     drawCard, shuffleDeck, fetchBasicLand, toggleDeckOpenPlay
 *   Seite: pingCard(instanceId), openViewer(zone), openScryMill('scry' | 'mill'),
 *     boardPosition() → Weltpunkt für „Aufs Brett" (ohne: Server-Vorgabe),
 *     deckOpenPlay (boolean, Zustand von „Offen spielen")
 */

/** Würfel der Aktionsleiste und des Flächen-Menüs; `sides` geht unverändert an `rollDice`. */
export const DICE = [
  { sides: 4, label: 'W4' },
  { sides: 6, label: 'W6' },
  { sides: 8, label: 'W8' },
  { sides: 10, label: 'W10' },
  { sides: 12, label: 'W12' },
  { sides: 20, label: 'W20' },
  { sides: 100, label: 'W100' },
  { sides: 'coin', label: 'Münze' },
];

/** Standardländer: `landType` ist der Wert, den der Server für `playtest:deck:fetchBasicLand` annimmt. */
export const BASIC_LANDS = [
  { landType: 'Plains', label: 'Ebene' },
  { landType: 'Island', label: 'Insel' },
  { landType: 'Swamp', label: 'Sumpf' },
  { landType: 'Mountain', label: 'Gebirge' },
  { landType: 'Forest', label: 'Wald' },
  { landType: 'Wastes', label: 'Ödnis' },
];

/** Ziele einer Karte außerhalb ihrer Zone, in Menü-Reihenfolge. */
const ZONE_TARGETS = [
  { id: 'to-battlefield', toZone: 'battlefield', label: 'Aufs Brett', icon: Play },
  { id: 'to-hand', toZone: 'hand', label: 'Zur Hand', icon: Hand, shortcut: 'H' },
  { id: 'to-deck-top', toZone: 'deck', insertAtTop: true, label: 'Bibliothek oben', icon: ArrowUpToLine },
  { id: 'to-deck-bottom', toZone: 'deck', insertAtTop: false, label: 'Bibliothek unten', icon: ArrowDownToLine, shortcut: 'D' },
  { id: 'to-graveyard', toZone: 'graveyard', label: 'Friedhof', icon: Skull, shortcut: 'F' },
  { id: 'to-exile', toZone: 'exile', label: 'Exil', icon: Sparkles, shortcut: 'E' },
];

/** Hängt Gruppen aneinander; die erste Zeile jeder weiteren nicht leeren Gruppe trägt die Trennlinie. */
function grouped(...groups) {
  const result = [];
  groups.map((group) => group.filter(Boolean)).filter((group) => group.length > 0).forEach((group, index) => {
    group.forEach((entry, entryIndex) => {
      result.push(index > 0 && entryIndex === 0 ? { ...entry, separatorBefore: true } : entry);
    });
  });
  return result;
}

const sameUser = (a, b) => a != null && b != null && String(a) === String(b);

function pingEntry(card, ctx) {
  return ctx.pingCard && {
    id: 'ping', label: 'Hinweis geben', icon: Bell, shortcut: 'P', onSelect: () => ctx.pingCard(card.instanceId),
  };
}

function faceDownEntry(card, ctx) {
  return ctx.setFaceDown && {
    id: 'face-down',
    label: card.faceDown ? 'Aufdecken' : 'Verdecken',
    icon: card.faceDown ? Eye : EyeOff,
    onSelect: () => ctx.setFaceDown(card.instanceId, !card.faceDown),
  };
}

function transformEntry(card, ctx) {
  return ctx.transformCard && card.isTransformCard === true && !card.faceDown && {
    id: 'transform',
    label: card.scryfallData?.imageOrientation === 'flip' ? 'Umdrehen' : 'Transformieren',
    icon: Repeat2,
    onSelect: () => ctx.transformCard(card.instanceId),
  };
}

function copyEntry(card, ctx) {
  // Der Server lehnt Kopien verdeckter Karten ab; die Lage der Kopie wählt er selbst.
  return ctx.createCardCopy && !card.faceDown && {
    id: 'copy', label: 'Kopieren', icon: Copy, onSelect: () => ctx.createCardCopy(card.instanceId),
  };
}

/** Untermenü „Counter": je Typ +1 (mit Stand), danach −1 für jeden gesetzten Typ. */
function counterEntry(card, ctx) {
  if (!ctx.addCounter) return null;
  const counters = card.counters || {};
  const add = CARD_COUNTER_ICONS.map(([type, icon]) => ({
    id: `counter-add-${type}`,
    label: COUNTER_LABELS[type],
    icon,
    badge: counters[type] > 0 ? counters[type] : undefined,
    onSelect: () => ctx.addCounter(card.instanceId, type),
  }));
  const remove = ctx.removeCounter
    ? CARD_COUNTER_ICONS.filter(([type]) => counters[type] > 0).map(([type]) => ({
      id: `counter-remove-${type}`,
      label: `${COUNTER_LABELS[type]} entfernen`,
      icon: Minus,
      onSelect: () => ctx.removeCounter(card.instanceId, type),
    }))
    : [];
  return { id: 'counters', label: 'Counter', icon: CircleDot, children: grouped(add, remove) };
}

/** Untermenü „Marker": jeder Marker schaltet um, „Alle entfernen" nur bei gesetzten. */
function markerEntry(card, ctx) {
  if (!ctx.addMarker || !ctx.removeMarker) return null;
  const markers = card.markers || {};
  const toggles = CARD_MARKER_ICONS.map(([type, icon]) => ({
    id: `marker-${type}`,
    label: MARKER_LABELS[type],
    icon,
    checked: !!markers[type],
    onSelect: () => (markers[type] ? ctx.removeMarker(card.instanceId, type) : ctx.addMarker(card.instanceId, type)),
  }));
  const clear = Object.values(markers).some(Boolean) && {
    id: 'marker-clear', label: 'Alle entfernen', icon: Trash2, danger: true, onSelect: () => ctx.removeMarker(card.instanceId, 'all'),
  };
  return { id: 'markers', label: 'Marker', icon: Tag, children: grouped([clear], toggles) };
}

/** In der Bibliothek selbst bleiben „oben" und „unten" Ziele: so sortiert Scry/Durchsehen um. */
function zoneMoveEntries(card, fromZone, ctx, { toBattlefield = false } = {}) {
  if (!ctx.moveCardZone) return [];
  return ZONE_TARGETS
    .filter((target) => (target.toZone !== fromZone || fromZone === 'deck') && (toBattlefield || target.toZone !== 'battlefield'))
    .map(({ toZone, insertAtTop, shortcut, ...entry }) => ({
      ...entry,
      // Die Tasten der Seite wirken nur auf Brettkarten; anderswo wäre der Hinweis falsch.
      ...(fromZone === 'battlefield' && shortcut && { shortcut }),
      onSelect: () => ctx.moveCardZone({
        instanceId: card.instanceId,
        fromZone,
        toZone,
        ...(insertAtTop !== undefined && { insertAtTop }),
        ...(toZone === 'battlefield' && { position: ctx.boardPosition?.() }),
      }),
    }));
}

/** Eigene (oder kontrollierte) Brettkarte. */
export function battlefieldCardActions(card, ctx) {
  const controlled = card.controllerId != null && !sameUser(card.controllerId, card.ownerId);
  return grouped(
    [pingEntry(card, ctx)],
    [
      ctx.tapCard && {
        id: 'tap', label: card.tapped ? 'Enttappen' : 'Tappen', icon: RotateCw, shortcut: 'T', onSelect: () => ctx.tapCard(card.instanceId),
      },
      faceDownEntry(card, ctx),
      transformEntry(card, ctx),
    ],
    [counterEntry(card, ctx), markerEntry(card, ctx), copyEntry(card, ctx)],
    zoneMoveEntries(card, 'battlefield', ctx),
    [controlled && ctx.returnCardControl && {
      id: 'return-control', label: 'Kontrolle zurückgeben', icon: Replace, onSelect: () => ctx.returnCardControl(card.instanceId),
    }],
  );
}

/** Brettkarte eines anderen Teilnehmers; die Anfrage geht an den, auf dessen Brett sie liegt. */
export function opponentCardActions(card, ctx) {
  const holder = card.controllerId ?? card.ownerId;
  return grouped(
    [pingEntry(card, ctx)],
    [
      copyEntry(card, ctx),
      ctx.requestCardControl && holder != null && {
        id: 'request-control', label: 'Kontrolle anfragen', icon: Replace, onSelect: () => ctx.requestCardControl(holder, card.instanceId),
      },
    ],
  );
}

/** Eigene Handkarte. */
export function handCardActions(card, ctx) {
  return grouped(
    [faceDownEntry(card, ctx), transformEntry(card, ctx)],
    zoneMoveEntries(card, 'hand', ctx),
  );
}

/**
 * Brettkarte für einen Ausgeschiedenen: er sieht nur zu, darf aber weiter auf Karten zeigen
 * (der Server erlaubt den Hinweis jedem Teilnehmer).
 */
export function spectatorCardActions(card, ctx) {
  return grouped([pingEntry(card, ctx)]);
}

/**
 * Karte einer Zone (Bibliothek, Friedhof, Exil, Commander-Zone), etwa im Zonen-Viewer. In der
 * Bibliothek (Scry, Durchsehen) legen „Bibliothek oben/unten" die Karte innerhalb der Bibliothek um.
 */
export function zoneCardActions(card, zone, ctx) {
  const moves = zoneMoveEntries(card, zone, ctx, { toBattlefield: true });
  // Counter gibt es außerhalb des Bretts nur in Friedhof und Exil (Server wie Socket-Hook).
  const counters = zone === 'graveyard' || zone === 'exile' ? [counterEntry(card, ctx)] : [];
  return grouped(moves, counters);
}

/** Bibliotheks-Kachel. */
export function deckActions(ctx) {
  const draw = ctx.drawCard && [
    {
      id: 'draw',
      label: 'Ziehen',
      icon: Layers,
      children: [1, 2, 3, 4, 5, 6, 7].map((n) => ({
        id: `draw-${n}`, label: n === 1 ? '1 Karte' : `${n} Karten`, onSelect: () => ctx.drawCard(n),
      })),
    },
    { id: 'draw-face-down', label: 'Verdeckt ziehen', icon: EyeOff, onSelect: () => ctx.drawCard(1, true) },
  ];
  return grouped(
    draw || [],
    [
      ctx.shuffleDeck && { id: 'shuffle', label: 'Mischen', icon: Shuffle, onSelect: () => ctx.shuffleDeck() },
      ctx.openViewer && { id: 'browse', label: 'Durchsehen', icon: Search, onSelect: () => ctx.openViewer('deck') },
      ctx.openScryMill && { id: 'scry', label: 'Scry…', icon: Eye, onSelect: () => ctx.openScryMill('scry') },
      ctx.openScryMill && { id: 'mill', label: 'Mill…', icon: Skull, onSelect: () => ctx.openScryMill('mill') },
    ],
    [
      ctx.fetchBasicLand && {
        id: 'basic-land',
        label: 'Standardland holen',
        icon: Mountain,
        children: BASIC_LANDS.map(({ landType, label }) => ({
          id: `basic-land-${landType}`, label, icon: Mountain, onSelect: () => ctx.fetchBasicLand(landType),
        })),
      },
      ctx.toggleDeckOpenPlay && {
        id: 'open-play',
        label: ctx.deckOpenPlay ? 'Offen spielen aus' : 'Offen spielen an',
        icon: ctx.deckOpenPlay ? EyeOff : Eye,
        onSelect: () => ctx.toggleDeckOpenPlay(),
      },
    ],
  );
}

/** Würfel als Einträge; `onRoll(sides)`. */
export function diceActions(onRoll) {
  return DICE.map(({ sides, label }) => ({
    id: `dice-${sides}`, label, icon: sides === 'coin' ? Coins : Dices, onSelect: () => onRoll(sides),
  }));
}

/**
 * Freie Brettfläche (Rechtsklick bzw. Langdruck): nur Aktionen, die das Brett selbst betreffen
 * (Spec 2026-10-21 §5.6). `position` geht unverändert an `onCreateToken`.
 */
export function boardActions({ onCreateToken, onRollDice, onUntapAll }, position) {
  return [
    onCreateToken && { id: 'create-token', label: 'Token erzeugen', icon: Plus, onSelect: () => onCreateToken(position) },
    onRollDice && { id: 'dice', label: 'Würfeln', icon: Dices, children: diceActions(onRollDice) },
    onUntapAll && { id: 'untap-all', label: 'Alle enttappen', icon: RefreshCw, onSelect: () => onUntapAll() },
  ].filter(Boolean);
}

/**
 * „Mehr"-Liste der Aktionsleiste (Spec §5.6, §13 Teilnehmer-Status). Am Handy lässt die Seite
 * `onOpenShortcuts` weg. „Ich bin raus" fehlt für Ausgeschiedene; Zurücksetzen und Beenden gibt
 * es nur für den Host. „Verlassen" ändert keinen Status, deshalb ohne Rückfrage.
 */
export function sessionActions({
  isHost, playerStatus, onOverview, onOpenProtocol, onOpenShortcuts, onEliminate, onLeave, onReset, onEndSession,
}) {
  return grouped(
    [
      onOverview && { id: 'overview', label: 'Übersicht', icon: Maximize2, onSelect: () => onOverview() },
      onOpenProtocol && { id: 'protocol', label: 'Protokoll', icon: ScrollText, onSelect: () => onOpenProtocol() },
      onOpenShortcuts && { id: 'shortcuts', label: 'Tastenkürzel', icon: Keyboard, onSelect: () => onOpenShortcuts() },
    ],
    [
      onEliminate && playerStatus !== 'eliminated' && {
        id: 'eliminate',
        label: 'Ich bin raus',
        icon: Flag,
        danger: true,
        onSelect: () => onEliminate(),
        confirm: {
          title: 'Ich bin raus?',
          description: 'Du scheidest aus diesem Playtest aus und siehst weiter zu. Nur ein Zurücksetzen durch den Host hebt das auf.',
          confirmLabel: 'Ich bin raus',
        },
      },
      onLeave && { id: 'leave', label: 'Verlassen', icon: LogOut, onSelect: () => onLeave() },
    ],
    [
      isHost && onReset && {
        id: 'reset',
        label: 'Zurücksetzen',
        icon: RotateCcw,
        danger: true,
        onSelect: () => onReset(),
        confirm: {
          title: 'Playtest zurücksetzen?',
          description: 'Alle Teilnehmer beginnen von vorn mit ihrem Deck; Brett, Hand, Leben und Marken werden zurückgesetzt.',
          confirmLabel: 'Zurücksetzen',
        },
      },
      isHost && onEndSession && {
        id: 'end-session',
        label: 'Session beenden',
        icon: Power,
        danger: true,
        onSelect: () => onEndSession(),
        confirm: {
          title: 'Session beenden?',
          description: 'Der Playtest endet für alle Teilnehmer.',
          confirmLabel: 'Beenden',
        },
      },
    ],
  );
}
